import React, { useEffect, useState } from "react";
import { Box, Skeleton, Typography, useMediaQuery, useTheme, colors } from "@mui/material";
import MaterialTable from "../../../Components/MaterialTable";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import TableFilter from "../../../Components/MaterialTable/TableFilter";
import {
	useLazyGetAdminWeddingInvitationsQuery,
	usePublishWeddingInvitationMutation,
	useUnPublishWeddingInvitationMutation,
	useRejectWeddingInvitationMutation,
	useSuspendWeddingInvitationMutation,
	useActionRequiredWeddingInvitationMutation
} from "../../../store/services/admin/admin.service";
import {
	INVITATION_STATUS,
	INVITATION_STATUSES,
	Invitations
} from "../../../store/services/invitations/dto/invitations.dto";
import TableRowMenu from "../../../Components/MaterialTable/TableRowMenu";
import { InvitationIdCellWrapper } from "./styles";
import ConfirmModal from "../../../Components/Modals/ConfirmModal";
import { toast } from "react-toastify";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { formatDate } from "../../../utils/formatDate";
import { StyleCircle } from "../../../Components/Inputs/Settings/styles";
import { InvitationStatusColor } from "../../../constants";
import CardsList from "./CardsList";

const PER_PAGE = 10;
const InvitationsList = () => {
	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState("" + INVITATION_STATUS.DRAFT);
	const [fetchInvitations, { data, isFetching: isFetchingInvitations, isLoading: isLoadingInvitations }] =
		useLazyGetAdminWeddingInvitationsQuery();
	const [callPublishInvitation, { isLoading: isPublishingInvitation }] = usePublishWeddingInvitationMutation();
	const [callUnPublishInvitation, { isLoading: isUnPublishingInvitation }] = useUnPublishWeddingInvitationMutation();
	const [callRejectInvitation, { isLoading: isRejectingInvitation }] = useRejectWeddingInvitationMutation();
	const [callActionRequired, { isLoading: isAskingActionInvitation }] = useActionRequiredWeddingInvitationMutation();
	const [callSuspendInvitation, { isLoading: isSuspendingInvitation }] = useSuspendWeddingInvitationMutation();
	const [confirmPublish, setConfirmPublish] = useState<string | null>(null);
	const [confirmUnPublish, setConfirmUnPublish] = useState<string | null>(null);
	const [confirmReject, setConfirmReject] = useState<string | null>(null);
	const [confirmActionRequired, setConfirmActionRequired] = useState<string | null>(null);
	const [confirmSuspend, setConfirmSuspend] = useState<string | null>(null);
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

	useEffect(() => {
		fetchInvitations({ page: page, perPage: PER_PAGE, status: +statusFilter, sortBy: { createdAt: -1 } });
	}, []);

	const handlePageChange = (page: number) => {
		if (page > 0) {
			setPage(page);
			fetchInvitations({ page: page, perPage: PER_PAGE, status: +statusFilter, sortBy: { createdAt: -1 } });
		}
	};

	const tableColumns: Array<IColumn<Invitations>> = [
		{
			label: "InvitationId",
			dataKey: "invitation_id",
			Cell: ({ rowData }) => (
				<InvitationIdCellWrapper to={`/manageWeddingInvitation/${rowData._id}`} target={"_blank"}>
					{rowData._id}
				</InvitationIdCellWrapper>
			)
		},
		{
			label: "template",
			dataKey: "invitation_template",
			Cell: ({ rowData }) => <div>{rowData.template_id}</div>
		},
		{
			label: "Status",
			dataKey: "status",
			Cell: ({ rowData }) => (
				<Box display={"flex"} gap={1} alignItems={"center"}>
					<StyleCircle $color={InvitationStatusColor[rowData.status || 1]} />
					<Typography variant={"caption"}>{INVITATION_STATUSES[rowData.status]}</Typography>
				</Box>
			)
		},
		{
			label: "Responses",
			dataKey: "responses",
			Cell: ({ rowData }) => (
				<Box display={"flex"} flexDirection={"column"} alignItems={"flex-start"}>
					<Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
						<Typography variant={"caption"}>Views</Typography>
						<Typography variant={"caption"}>{rowData.responseStats?.total_views}</Typography>
					</Box>
					<Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
						<Typography variant={"caption"}>Total</Typography>
						<Typography variant={"caption"}>{rowData.responseStats?.total}</Typography>
					</Box>
					<Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
						<Typography variant={"caption"} style={{ color: colors.green[800] }}>
							Accepted
						</Typography>
						<Typography variant={"caption"} style={{ color: colors.green[800] }}>
							{rowData.responseStats?.accepted}
						</Typography>
					</Box>
					<Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
						<Typography variant={"caption"} style={{ color: colors.red[800] }}>
							Declined
						</Typography>
						<Typography variant={"caption"} style={{ color: colors.red[800] }}>
							{rowData.responseStats?.declined}
						</Typography>
					</Box>
				</Box>
			)
		},
		{
			label: "User",
			dataKey: "user",
			Cell: ({ rowData }) => (
				<Box display={"flex"} flexDirection={"column"}>
					<Box display={"flex"} gap={1}>
						<Typography>{rowData.user_id?.firstName}</Typography>
						<Typography>{rowData.user_id?.lastName}</Typography>
					</Box>
					<Typography>{rowData.user_id?.email}</Typography>
				</Box>
			)
		},
		{
			label: "Wedding Date",
			dataKey: "wedding_date",
			Cell: ({ rowData }) => <Box width={"150px"}>{formatDate(rowData.wedding_date, rowData.timezone)}</Box>
		},
		{
			label: "Date created",
			dataKey: "createdAt",
			Cell: ({ rowData }) => <Box width={"150px"}>{formatDate(rowData.createdAt, rowData.timezone)}</Box>
		},
		{
			label: "",
			dataKey: "menu",
			Cell: ({ rowData }) => {
				return (
					<TableRowMenu
						items={[
							{
								label: "Publish",
								visible: rowData.status === INVITATION_STATUS.PENDING,
								onSelected: () => {
									setConfirmPublish(rowData._id);
								}
							},
							{
								label: "Reject",
								visible: rowData.status === INVITATION_STATUS.PENDING,
								onSelected: () => {
									setConfirmReject(rowData._id);
								}
							},
							{
								label: "Action Required",
								visible: rowData.status === INVITATION_STATUS.PENDING,
								onSelected: () => {
									setConfirmActionRequired(rowData._id);
								}
							},
							{
								label: "Un Publish",
								visible: rowData.status === INVITATION_STATUS.PUBLISHED,
								onSelected: () => {
									setConfirmUnPublish(rowData._id);
								}
							},
							{
								label: "Suspend",
								visible: rowData.status === INVITATION_STATUS.PUBLISHED,
								onSelected: () => {
									setConfirmSuspend(rowData._id);
								}
							}
						]}
					/>
				);
			}
		}
	];

	const handlePublish = () => {
		if (!confirmPublish) return;
		callPublishInvitation({ id: confirmPublish }).then(({ data, error }) => {
			if (data) {
				toast.success("Invitation has been Published", { autoClose: 3000 });
				setConfirmPublish(null);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleUnPublish = () => {
		if (!confirmUnPublish) return;
		callUnPublishInvitation({ id: confirmUnPublish }).then(({ data, error }) => {
			if (data) {
				toast.success("Invitation has been Un Published", { autoClose: 3000 });
				setConfirmUnPublish(null);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleReject = () => {
		if (!confirmReject) return;
		callRejectInvitation({ id: confirmReject }).then(({ data, error }) => {
			if (data) {
				toast.success("Invitation has been Rejected", { autoClose: 3000 });
				setConfirmReject(null);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};
	const handleActionRequired = () => {
		if (!confirmActionRequired) return;
		callActionRequired({ id: confirmActionRequired }).then(({ data, error }) => {
			if (data) {
				toast.success("User Action have been requested", { autoClose: 3000 });
				setConfirmActionRequired(null);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};
	const handleSuspend = () => {
		if (!confirmSuspend) return;
		callSuspendInvitation({ id: confirmSuspend }).then(({ data, error }) => {
			if (data) {
				toast.success("Invitation has been suspended", { autoClose: 3000 });
				setConfirmSuspend(null);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const Filters = (
		<TableFilter
			filterLabel={"Status"}
			filterVal={statusFilter}
			options={[
				{ label: INVITATION_STATUSES[INVITATION_STATUS.DRAFT], value: "" + INVITATION_STATUS.DRAFT },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.PENDING], value: "" + INVITATION_STATUS.PENDING },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.REJECTED], value: "" + INVITATION_STATUS.REJECTED },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.PUBLISHED], value: "" + INVITATION_STATUS.PUBLISHED },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.SUSPENDED], value: "" + INVITATION_STATUS.SUSPENDED },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.DELETED], value: "" + INVITATION_STATUS.DELETED },
				{ label: INVITATION_STATUSES[INVITATION_STATUS.ACTION_REQUIRED], value: "" + INVITATION_STATUS.ACTION_REQUIRED }
			]}
			onChange={val => {
				setStatusFilter(val);
				fetchInvitations({ page: 1, perPage: PER_PAGE, status: +val, sortBy: { createdAt: -1 } });
			}}
		/>
	);

	const LoadingSkeleton = () => {
		const loadingArray = new Array(6).fill(0);
		return (
			<Box display="flex" style={{ flexFlow: "row wrap" }} gap={2}>
				{loadingArray.map((_, index) => (
					<Box key={index} width={"100%"}>
						<Skeleton variant={"rectangular"} height={"100px"} />
					</Box>
				))}
			</Box>
		);
	};
	return (
		<Box mb={3}>
			{isSmallerScreen ? (
				<Box>
					<Box display={"flex"} justifyContent={"flex-end"}>
						{Filters}
					</Box>
					{isFetchingInvitations || isLoadingInvitations ? (
						<LoadingSkeleton />
					) : (
						<CardsList
							data={data?.items || []}
							page={page}
							perPage={PER_PAGE}
							totalItems={data?.totalItems || 0}
							setConfirmPublish={setConfirmPublish}
							setConfirmUnPublish={setConfirmUnPublish}
							setConfirmReject={setConfirmReject}
							setConfirmActionRequired={setConfirmActionRequired}
							setConfirmSuspend={setConfirmSuspend}
							onPageChange={handlePageChange}
						/>
					)}
				</Box>
			) : (
				<MaterialTable
					tableLabel={"Invitations"}
					columns={tableColumns}
					data={data?.items || []}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={data?.totalItems}
					onPageChange={handlePageChange}
					isLoading={isFetchingInvitations || isLoadingInvitations}
					filters={<Box>{Filters}</Box>}
				/>
			)}
			{confirmPublish && (
				<ConfirmModal
					open={!!confirmPublish}
					title={"Publish Invitation"}
					message={"Are you sure you want to publish this invitation?"}
					onConfirm={handlePublish}
					onClose={() => setConfirmPublish(null)}
					confirmText={"Yes, Publish"}
					cancelText={"No, Cancel"}
					loading={isPublishingInvitation}
				/>
			)}{" "}
			{confirmUnPublish && (
				<ConfirmModal
					open={!!confirmUnPublish}
					title={"Un Publish Invitation"}
					message={"Are you sure you want to un-publish this invitation?"}
					onConfirm={handleUnPublish}
					onClose={() => setConfirmUnPublish(null)}
					confirmText={"Yes, Un Publish"}
					cancelText={"No, Cancel"}
					loading={isUnPublishingInvitation}
				/>
			)}{" "}
			{confirmReject && (
				<ConfirmModal
					open={!!confirmReject}
					title={"Reject Invitation"}
					message={"Are you sure you want to reject this invitation?"}
					onConfirm={handleReject}
					onClose={() => setConfirmReject(null)}
					confirmText={"Yes, Reject"}
					cancelText={"No, Cancel"}
					loading={isRejectingInvitation}
				/>
			)}{" "}
			{confirmActionRequired && (
				<ConfirmModal
					open={!!confirmActionRequired}
					title={"Invitation Action Required"}
					message={"Are you sure you want to ask for user's action?"}
					onConfirm={handleActionRequired}
					onClose={() => setConfirmActionRequired(null)}
					confirmText={"Yes, Ask Action"}
					cancelText={"No, Cancel"}
					loading={isAskingActionInvitation}
				/>
			)}
			{confirmSuspend && (
				<ConfirmModal
					open={!!confirmSuspend}
					title={"Suspend Invitation"}
					message={"Are you sure you want to suspend this invitation?"}
					onConfirm={handleSuspend}
					onClose={() => setConfirmSuspend(null)}
					confirmText={"Yes, Suspend"}
					cancelText={"No, Cancel"}
					loading={isSuspendingInvitation}
				/>
			)}
		</Box>
	);
};

export default InvitationsList;
